<template>
    <label class="mb-2 text-base font-gotham font-medium">
        {{ $t('ENTER_EMBROIDERY') }}
    </label>

    <div class="grid grid-flow-row grid-cols-2 gap-7">
        <base-input-select
            :select-options="fonts"
            @input="selectFont"
        />

        <div>
            <base-input
                input-name="personalizationText"
                input-placeholder="ABC"
                :input-value="personalizationText"
                :input-message="$t(`MAX_LENGTH_${personalizationLimit}`)"
                :message-error="maxLengthExceeded"
                :input-max-length="personalizationLimit"
                :error-message="profanityError"
                :show-error-message="profanityError !== '' || maxLengthExceeded"
                :is-korean-invalid-message="isKoreanInvalidMessage"
                @input="updateText"
            />
        </div>
    </div>

    <div class="mt-7 mb-2 flex justify-between text-sm font-gotham">
        <label class="font-bold mr-2">
            {{$t('MONOGRAM_COLOR')}}
        </label>

        <label
            v-if="selectedColorName"
            class="font-light overflow-hidden whitespace-nowrap text-ellipsis"
        >
            {{selectedColorName}}
        </label>
    </div>

    <swatch-button-group
        :option-name="placement.code"
        :options="colors"
        :selected-value="selectedColorValue"
        :variant="'small'"
        :hide-labels="true"
        @selection-changed="selectColor"
    ></swatch-button-group>
</template>

<script>
    import SwatchButtonGroup from './SwatchButtonGroup.vue';
    import BaseInput from './BaseInput.vue';
    import BaseInputSelect from './BaseInputSelect.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            BaseInput,
            BaseInputSelect,
            SwatchButtonGroup,
        },

        props: {
            placement: {
                type: Object,
                required: true,
            },
            mirror: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                personalizationText: '',
                personalizationLimit: 6,
                maxLengthExceeded: false,
                profanityError: '',
                currentFont: '',
                isKoreanInvalidMessage: false,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            optionGroups() {
                const placementGroups = this.viewController.groupByDefinitions(this.placement, {
                    includeUnavailable: true,
                });

                return placementGroups;
            },

            fonts() {
                const fontGroup = this.optionGroups.find((x) => x.description.definition === 'Monogramming Font');

                if (fontGroup) {
                    const fonts = fontGroup.options.filter((option) => !option.placeholder).map((option) => ({
                        label: option.option.name,
                        code: option.option.code,
                        selected: option.selected,
                    }));

                    return fonts;
                }

                return [];
            },

            colors() {
                const colorGroup = this.optionGroups.find((x) => x.description.definition === 'Monogramming Color');

                if (colorGroup) {
                    const colors = colorGroup.options.filter((option) => !option.placeholder).map((option) => ({
                        label: option.option.name,
                        code: option.option.code,
                        selected: option.selected,
                        custom: {
                            hex: option.option.custom.hex,
                        },
                    }));

                    return colors;
                }

                return [];
            },

            selectedColorValue() {
                return this.colors.find((x) => x.selected)?.code;
            },

            selectedColorName() {
                return this.colors.find((x) => x.selected)?.label;
            },

            locale() {
                return this.customizerStore.options?.locale;
            },

            isKorean() {
                return this.locale.toLowerCase().includes('kr');
            },
        },

        mounted() {
            this.syncText();

            this.currentFont = this.fonts[0].code;
        },

        methods: {
            syncText() {
                const selected = this.viewController.selectedAtPlacement(this.placement.code);

                if (selected) {
                    if (selected.custom?.['personalization-limit']) {
                        this.personalizationLimit = +selected.custom['personalization-limit'];
                    }

                    if (selected.custom?.['personalization-text']) {
                        this.personalizationText = selected?.custom?.['personalization-text'];
                    }
                }
            },

            findOtherSide() {
                let otherSide;

                if (this.placement.code === 'MONOGRAMMINGTEXT11') {
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGTEXT21');
                } else {
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGTEXT11');
                }

                return otherSide;
            },

            selectFont(font) {
                this.customizerStore.undoCheckpoint();

                this.viewController.updateSimilarComponent(this.placement.code, 'Monogramming Font', font.code);

                this.syncText();

                const otherSide = this.findOtherSide();

                this.viewController.updateSimilarComponent(otherSide.code, 'Monogramming Font', font.code);

                this.currentFont = font.code;
                this.checkIfScript();
            },

            selectColor(color) {
                this.customizerStore.undoCheckpoint();

                this.viewController.updateSimilarComponent(this.placement.code, 'Monogramming Color', color.value);

                this.syncText();

                const otherSide = this.findOtherSide();

                this.viewController.updateSimilarComponent(otherSide.code, 'Monogramming Color', color.value);
            },

            updateText(text) {
                // Hmmm...
                // We probably need to update our patterns...
                //
                // `updatePersonalization` can modify text, for example perform uppercase
                // transformations, or trimming text to length.
                // Means that we have to validate text _after_ sending it off to the controller.
                //
                // Second problem, since the validation happens after setting it in the controller,
                // and we don't have a separate validation state for embroidery, we'd have to revert to the last "safe" text.
                // Also, we need to debounce the validation event, otherwise we are DDoSing ourselves.
                //
                // Third problem... mirroring the text to the "other" side also happens after the updatePersonalization call
                // which means we also have to validate the "other" side component.

                this.personalizationText = text;

                // First, preview the text value.
                this.viewController.updatePersonalization(this.placement.code, text, {
                    preview: true,
                }).then((result) => {
                    // Validation failed anyway.
                    if (!result.accepted) {
                        this.personalizationText = result.lastValid;
                    } else {
                        // General validation passed, and the text may have been transformed. Sync the text box.
                        this.personalizationText = result.acceptedAs;

                        // Now we can validate.
                        this.checkForProfanity(result.acceptedAs);
                    }

                    this.checkIfScript();
                });
            },

            checkForProfanity(text) {
                // Debounce the validation event.
                if (this.moderationTimer) {
                    clearTimeout(this.moderationTimer);
                }

                this.moderationTimer = setTimeout(() => {
                    if (text !== '') {
                        this.viewController.reviewText(text).then((result) => {
                            if (result.hasProfanity) {
                                this.profanityError = `${this.$t('INVALID_ERROR')}`;
                                this.isKoreanInvalidMessage = this.isKorean;
                            } else {
                                this.profanityError = '';
                                this.isKoreanInvalidMessage = false;

                                // And now we can finally set the text.
                                this.customizerStore.undoCheckpoint();

                                this.viewController.updatePersonalization(this.placement.code, text).then((updateResult) => {
                                    if (updateResult.accepted) {
                                        if (this.mirror) {
                                            const otherSide = this.findOtherSide();

                                            this.viewController.updatePersonalization(otherSide.code, updateResult.acceptedAs);
                                        }
                                    }
                                });
                            }
                        }).catch(() => {});
                    } else {
                        this.profanityError = '';
                        this.isKoreanInvalidMessage = false;
                        this.viewController.updatePersonalization(this.placement.code, text).then((updateResult) => {
                            if (updateResult.accepted) {
                                if (this.mirror) {
                                    const otherSide = this.findOtherSide();

                                    this.viewController.updatePersonalization(otherSide.code, '');
                                }
                            }
                        });
                    }
                }, 300);
            },

            checkIfScript() {
                // Put input into error state if script font is selected and text length exceeds the limit
                if (this.currentFont.includes('SCRIPT')) {
                    if (this.personalizationText.length > this.personalizationLimit) {
                        this.maxLengthExceeded = true;
                    } else {
                        this.maxLengthExceeded = false;
                    }
                } else {
                    this.maxLengthExceeded = false;
                }
            },
        },
    };
</script>
